import * as yup from "yup"
import { validateField, validateFields } from "src/modules/validation"
import { Career } from "src/openapi"

export const validateGeneralInformation = async (
  firstName: string,
  lastName: string,
  firstNameJP: string,
  lastNameJP: string,
  jobs: number[],
  birthday: string,
  phoneNumber: string,
  avatarPath: string,
  postcode: string,
  city: string,
  setNameValidationMessage: any,
  setJobValidationMessage: any,
  setBirthdayValidationMessage: any,
  setPhoneNumberValidationMessage: any,
  setAvatarPathValidationMessage: any,
  setPostCodeValidationMessage: any,
  setCityValidationMessage: any,
  translation: any
) => {
  const t = translation

  let validation = true
  try {
    await validateFields(
      yup.object().shape({
        firstName: yup.string().required(t("please_input_first_name")),
        lastName: yup.string().required(t("please_input_last_name")),
        firstNameJP: yup
          .string()
          .required(t("please_input_first_name_jp"))
          .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        lastNameJP: yup
          .string()
          .required(t("please_input_last_name_jp"))
          .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
      }),
      ["firstName", "lastName", "firstNameJP", "lastNameJP"],
      [firstName, lastName, firstNameJP, lastNameJP]
    )
    validation = validation && true
    setNameValidationMessage("")
  } catch (err: any) {
    setNameValidationMessage(err.message)
    validation = validation && false
  }

  if (jobs.length === 0) {
    setJobValidationMessage(t("please_input_job"))
    validation = validation && false
  } else {
    setJobValidationMessage("")
  }

  try {
    await validateField(
      yup.object().shape({
        birthday: yup
          .string()
          .required(t("please_input_birthday"))
          .matches(/^(\d{4})\-(\d{1,2})\-(\d{1,2})$/, t("please_input_correct_birthday")),
      }),
      "birthday",
      birthday
    )
    setBirthdayValidationMessage("")
  } catch (err: any) {
    setBirthdayValidationMessage(err.message)
    validation = validation && false
  }

  try {
    await validateField(
      yup.object().shape({
        phoneNumber: yup
          .string()
          .required(t("please_input_phone_number"))
          .matches(
            /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
            t("please_input_correct_phone_number")
          ),
      }),
      "phoneNumber",
      phoneNumber
    )
    setPhoneNumberValidationMessage("")
  } catch (err: any) {
    setPhoneNumberValidationMessage(err.message)
    validation = validation && false
  }

  if (avatarPath) {
    setAvatarPathValidationMessage("")
  } else {
    setAvatarPathValidationMessage(t("please_select_file"))
  }

  try {
    await validateField(
      yup.object().shape({
        postcode: yup
          .string()
          .required(t("please_input_post_code"))
          .matches(/〒?[0-9]{3}-?[0-9]{4}/g, t("please_input_correct_post_code")),
      }),
      "postcode",
      postcode
    )
    setPostCodeValidationMessage("")
  } catch (err: any) {
    setPostCodeValidationMessage(err.message)
    validation = validation && false
  }

  try {
    await validateField(
      yup.object().shape({
        city: yup.string().required(t("please_input_city")),
      }),
      "city",
      city
    )
    setCityValidationMessage("")
  } catch (err: any) {
    setCityValidationMessage(err.message)
    validation = validation && false
  }

  return validation
}

export const validateLoginInformation = async (
  email: string,
  password: string,
  passwordConfirm: string,
  setEmailValidationMessage: any,
  setPasswordValidationMessage: any,
  setPasswordConfirmValidationMessage: any,
  translation: any
) => {
  let t = translation
  let validation = true

  try {
    await validateField(
      yup.object().shape({
        email: yup
          .string()
          .required(t("please_input_email"))
          .email(t("please_input_correct_email")),
      }),
      "email",
      email
    )
    setEmailValidationMessage("")
  } catch (err: any) {
    setEmailValidationMessage(err.message)
    validation = validation && false
  }

  try {
    await validateField(
      yup.object().shape({
        password: yup
          .string()
          .required(t("please_input_password"))
          .min(8, t("password_length_min"))
          .max(20, t("password_length_max"))
          .test("isValidPassword", t("password_regex"), (value) => {
            const hasLowerCase = /[a-z]/.test(value)
            const hasNumber = /[0-9]/.test(value)

            return hasLowerCase && hasNumber
          }),
      }),
      "password",
      password
    )
    setPasswordValidationMessage("")
  } catch (err: any) {
    setPasswordValidationMessage(err.message)
    validation = validation && false
  }

  if (password === passwordConfirm) {
    setPasswordConfirmValidationMessage("")
  } else {
    setPasswordConfirmValidationMessage(t("password_password_confirm_different"))
    validation = validation && false
  }

  return validation
}

export const validatePRInformation = async (
  abstract: string,
  setAbstractValidationMessage: any,
  message: string,
  setMessageValidationMessage: any,
  career: Career[],
  setCareerValidationMessage: any,
  lounge: boolean,
  setLoungeValidationMessage: any,
  resident: boolean,
  setResidentValidationMessage: any,
  translation: any
) => {
  let t = translation
  let validation = true

  try {
    await validateField(
      yup.object().shape({
        abstract: yup.string().required(t("please_input_abstract")),
      }),
      "abstract",
      abstract
    )
    setAbstractValidationMessage("")
  } catch (err: any) {
    setAbstractValidationMessage(err.message)
    validation = validation && false
  }

  try {
    await validateField(
      yup.object().shape({
        message: yup.string().required(t("please_input_message")),
      }),
      "message",
      message
    )
    setMessageValidationMessage("")
  } catch (err: any) {
    setMessageValidationMessage(err.message)
    validation = validation && false
  }

  for (let i = 0; i < career.length; i++) {
    if (!career[i].career) {
      setCareerValidationMessage(t("please_input_correct_career"))
      validation = validation && false
      break
    }
    setCareerValidationMessage("")
  }

  if (lounge === undefined) {
    setLoungeValidationMessage(t("please_select_lounge"))
    validation = validation && false
  } else {
    setLoungeValidationMessage("")
  }

  if (resident === undefined) {
    setResidentValidationMessage(t("please_select_resident"))
    validation = validation && false
  } else {
    setResidentValidationMessage("")
  }

  return validation
}
